import React, { useState } from 'react';
import axios from 'axios';

const LiveUpdates = () => {
  const [selectedGame, setSelectedGame] = useState('');
  const [gameDetails, setGameDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newsItems, setNewsItems] = useState([]);

  const fetchGameDetails = async () => {
    if (!selectedGame) {
      setError('Please enter a game name');
      return;
    }

    setLoading(true);
    setError('');
    setNewsItems([]);
    try {
      // Fetch game details from RAWG
      const response = await axios.get(
        `https://api.rawg.io/api/games`,
        {
          params: {
            key: process.env.REACT_APP_RAWG_API_KEY,
            search: selectedGame,
            page_size: 1
          }
        }
      );

      if (response.data.results.length > 0) {
        const gameId = response.data.results[0].id;
        
        // Get detailed game info
        const detailedResponse = await axios.get(
          `https://api.rawg.io/api/games/${gameId}`,
          {
            params: {
              key: process.env.REACT_APP_RAWG_API_KEY
            }
          }
        );

        setGameDetails(detailedResponse.data);

        // Get game updates from RAWG
        const updatesResponse = await axios.get(
          `https://api.rawg.io/api/games/${gameId}/reddit`,
          {
            params: {
              key: process.env.REACT_APP_RAWG_API_KEY
            }
          }
        );

        // Transform Reddit posts into news items
        const redditPosts = updatesResponse.data.results || [];
        const newsItems = redditPosts.map(post => ({
          title: post.name || 'Game Update',
          description: post.text || 'No description available',
          publishedAt: new Date(post.created).toISOString(),
          url: post.url || null
        }));

        if (newsItems.length === 0) {
          // Fallback to getting game updates from the detailed game info
          const updates = [];
          
          // Add release date as an update if available
          if (detailedResponse.data.released) {
            updates.push({
              title: 'Game Release',
              description: `${detailedResponse.data.name} was released on ${new Date(detailedResponse.data.released).toLocaleDateString()}`,
              publishedAt: detailedResponse.data.released,
              url: detailedResponse.data.website || null
            });
          }

          // Add latest updates if available
          if (detailedResponse.data.updated) {
            updates.push({
              title: 'Latest Update',
              description: detailedResponse.data.description_raw 
                ? detailedResponse.data.description_raw.slice(0, 200) + '...'
                : 'Game information has been updated',
              publishedAt: detailedResponse.data.updated,
              url: detailedResponse.data.website || null
            });
          }

          // Add DLC/additions if available
          if (detailedResponse.data.additions_count > 0) {
            const dlcResponse = await axios.get(
              `https://api.rawg.io/api/games/${gameId}/additions`,
              {
                params: {
                  key: process.env.REACT_APP_RAWG_API_KEY
                }
              }
            );

            const dlcUpdates = dlcResponse.data.results.map(dlc => ({
              title: `New Content: ${dlc.name}`,
              description: dlc.description_raw 
                ? dlc.description_raw.slice(0, 200) + '...'
                : 'New content has been added to the game',
              publishedAt: dlc.released || detailedResponse.data.updated,
              url: dlc.website || detailedResponse.data.website || null
            }));

            updates.push(...dlcUpdates);
          }

          // Sort updates by date
          updates.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
          setNewsItems(updates);
        } else {
          setNewsItems(newsItems);
        }

      } else {
        setError('Game not found');
      }
    } catch (error) {
      console.error('Error fetching game details:', error);
      setError('Error fetching game details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6 p-4 bg-xkid-gray rounded-lg border border-xkid-green">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-xkid-green mb-4">Game Details & News</h2>
        <p className="text-gray-400">Get detailed information and latest news about your favorite games!</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xkid-green mb-2">Game Name</label>
          <input
            type="text"
            value={selectedGame}
            onChange={(e) => setSelectedGame(e.target.value)}
            placeholder="Enter game name..."
            className="w-full p-3 rounded-lg bg-xkid-gray-light text-white border border-xkid-green focus:outline-none focus:ring-2 focus:ring-xkid-green"
          />
        </div>

        <button
          onClick={fetchGameDetails}
          disabled={loading}
          className="w-full p-3 rounded-lg bg-xkid-green text-black font-bold hover:bg-xkid-green-dark transition-colors disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Get Details & News'}
        </button>

        {error && (
          <div className="p-4 rounded-lg bg-xkid-red bg-opacity-20 text-xkid-red">
            {error}
          </div>
        )}
      </div>

      {gameDetails && (
        <div className="space-y-6 mt-8">
          <div className="p-4 rounded-lg border border-xkid-green bg-xkid-gray-light">
            <div className="flex flex-col md:flex-row gap-6">
              {gameDetails.background_image && (
                <img
                  src={gameDetails.background_image}
                  alt={gameDetails.name}
                  className="w-full md:w-1/3 h-48 md:h-auto object-cover rounded-lg"
                />
              )}
              <div className="flex-1">
                <h3 className="text-xl font-bold text-xkid-green">{gameDetails.name}</h3>
                <p className="text-gray-300 mt-2">{gameDetails.description_raw}</p>
                <div className="mt-4 space-y-2">
                  <p className="text-gray-300">
                    <span className="text-xkid-green">Released:</span> {formatDate(gameDetails.released)}
                  </p>
                  <p className="text-gray-300">
                    <span className="text-xkid-green">Rating:</span> {gameDetails.rating}/5 ({gameDetails.ratings_count} ratings)
                  </p>
                  <p className="text-gray-300">
                    <span className="text-xkid-green">Metacritic:</span> {gameDetails.metacritic || 'N/A'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {gameDetails.genres?.map((genre) => (
                      <span
                        key={genre.id}
                        className="px-2 py-1 text-xs rounded-full bg-xkid-green text-black"
                      >
                        {genre.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {gameDetails.developers?.length > 0 && (
            <div className="p-4 rounded-lg border border-xkid-green bg-xkid-gray-light">
              <h4 className="font-bold text-xkid-green mb-2">Developers</h4>
              <div className="flex flex-wrap gap-2">
                {gameDetails.developers.map((dev) => (
                  <span key={dev.id} className="text-gray-300">{dev.name}</span>
                ))}
              </div>
            </div>
          )}

          {gameDetails.platforms?.length > 0 && (
            <div className="p-4 rounded-lg border border-xkid-green bg-xkid-gray-light">
              <h4 className="font-bold text-xkid-green mb-2">Available Platforms</h4>
              <div className="flex flex-wrap gap-2">
                {gameDetails.platforms.map((platform) => (
                  <span
                    key={platform.platform.id}
                    className="px-2 py-1 text-xs rounded-full bg-xkid-gray text-xkid-green border border-xkid-green"
                  >
                    {platform.platform.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {gameDetails.tags?.length > 0 && (
            <div className="p-4 rounded-lg border border-xkid-green bg-xkid-gray-light">
              <h4 className="font-bold text-xkid-green mb-2">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {gameDetails.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-2 py-1 text-xs rounded-full bg-xkid-gray text-xkid-green border border-xkid-green"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {newsItems.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-xkid-green">Latest News</h3>
              {newsItems.map((news, index) => (
                <div
                  key={index}
                  className="p-4 rounded-lg border border-xkid-green bg-xkid-gray-light"
                >
                  <h4 className="font-bold text-xkid-green">{news.title}</h4>
                  <p className="text-gray-300 mt-2">{news.description}</p>
                  <div className="flex justify-between items-center mt-4">
                    <span className="text-gray-400 text-sm">
                      {formatDate(news.publishedAt)}
                    </span>
                    <a
                      href={news.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 rounded bg-xkid-green text-black text-sm hover:bg-xkid-green-dark transition-colors"
                    >
                      Read More
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveUpdates; 